import { Field, Form, Formik, useFormikContext } from "formik";
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";

import { Button, Link as DSLink, addToast } from "@icg360/design-system";

import { InputField } from "components/shared/form-fields";
import { requestResetSchema } from "consts";
import { useKeystone } from "data/ss-data-hooks";
import { isError } from "data/ss-error";
import { trackEvent } from "utils";

import styles from "./request-reset.module.scss";

type RequestResetProps = {
  setErrorMessages: (errors: string[]) => void;
};

const RequestReset = ({ setErrorMessages }: RequestResetProps) => {
  const navigate = useNavigate();
  const keystone = useKeystone();

  const handleSubmit = useCallback(
    async ({ email }, { setSubmitting }) => {
      trackEvent("Reset Password Button Clicked", {
        accountEmail: email,
      });
      setSubmitting(true);
      const res = await keystone.requestPasswordReset(email);
      if (isError(res)) {
        const errMsg = "Unable to send password reset email.";
        setErrorMessages([errMsg]);
        trackEvent("Error Displayed (request password reset)", {
          errorCode: 400,
          errorMessage: errMsg,
        });
        return;
      }
      trackEvent("Forgot password - reset complete");
      addToast("Please check your email for a link to reset your password.", {
        icon: true,
      });
      navigate("/");
      setSubmitting(false);
    },
    [keystone, navigate, setErrorMessages],
  );

  return (
    <Formik
      initialValues={{ email: "" }}
      onSubmit={handleSubmit}
      validationSchema={requestResetSchema}
    >
      <RequestResetForm />
    </Formik>
  );
};

const RequestResetForm = () => {
  const { isValid, dirty, isSubmitting } = useFormikContext();
  return (
    <Form className={styles.resetForm}>
      <Field
        name="email"
        type="email"
        title="Email"
        component={InputField}
        placeholder="Enter email address"
        className={styles.formGroup}
      />

      <Button
        type="submit"
        disabled={!isValid || !dirty || isSubmitting}
        data-testid="password-reset-send-btn"
        fluid
      >
        Send
      </Button>

      <div className={styles.activateAccount}>
        New here?{" "}
        <DSLink
          href="/auth/signup"
          onClick={() => trackEvent("Start Registration")}
        >
          Activate your account
        </DSLink>
      </div>
    </Form>
  );
};

export default RequestReset;
